import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { SearchIcon } from '@estee/elc-icons';
import { translate } from '@estee/elc-service';
import { InputField } from '@estee/elc-inputs';
import { ContentSpacing, Colors, breakpoint } from '@estee/elc-base-theme';
import { classes } from '../utils/Classes';
import { Mobile, Desktop } from './common/SearchMediaQueries';

import {
    customInputThemeMobile,
    inputBorderMobileTheme,
    searchGnavIconButton
} from '../../theme/default-theme';

const SearchOverlayWrapper = styled.div`
    display: flex;
    position: relative;
    width: 100%;

    .elc-input-search-gnav-field {
        cursor: pointer;
        width: 100%;
        left: 0;
        right: 0;
        padding: 0 0 0 ${ContentSpacing.space40};
        height: ${ContentSpacing.space44};
        ${customInputThemeMobile};
        ${inputBorderMobileTheme};
        ${breakpoint('desktop')`
            min-width: 236px;
            height: 41px;
            padding: 10px 0 10px 38px;
        `};
    }

    .elc-gnav-search-input-button {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${ContentSpacing.space40};
        padding: ${ContentSpacing.space8};
        z-index: 1;
        [dir='rtl'] & {
            left: unset;
            right: 0;
        }
    }

    .elc-icon-search {
        :focus {
            outline: ${Colors.info} auto 5px;
        }
    }
`;

const SearchButton = styled.button`
    ${searchGnavIconButton}
`;

const MagnifierWrapper = styled.div`
    left: 9px;
    position: absolute;
    top: 10px;
    z-index: 1;
`;

export interface IOverlayIconProps {
    openSearchOverlay(): void;
    isSearchOverlayOpen: boolean;
    inputConfig: IInputProps;
    showMobileInput?: boolean;
    showDesktopInput?: boolean;
}

interface IInputProps {
    disabled: boolean;
    onChange(event: React.FormEvent<HTMLInputElement>): void;
    inputValue: string;
    onKeyUp(event: React.KeyboardEvent<HTMLInputElement>): void;
}

export interface ISearchMagnifier {
    isBesideInput: boolean;
    translations: {
        searchButtonLabel: string;
    };
    onClick?(): void;
    isSearchOverlayOpen?: boolean;
}

export interface IGnavSearchInput {
    openSearchOverlay?(): void;
    translations: {
        searchPlaceholderLabel: string;
    };
    inputConfig: IInputProps;
}

export const GnavInput = translate(['searchPlaceholderLabel'])(
    observer(({ openSearchOverlay, translations, inputConfig }: IGnavSearchInput) => (
        <InputField
            name="search-input-gnav"
            inputLabel=""
            inputType="text"
            placeholder={translations.searchPlaceholderLabel}
            customClassName={classes('input-search-gnav-field')}
            data-test-id="Common-Search"
            {...{
                ...inputConfig,
                onFocus: openSearchOverlay
            }}
        />
    ))
);

export const SearchMagnifier = translate(['searchButtonLabel'])(
    observer(({ onClick, isBesideInput, isSearchOverlayOpen, translations }: ISearchMagnifier) => {
        const svgIconSearchClassnames = classes('icon-search');
        const circleIconSearchClassnames = classes('icon-search-circle');
        const magnifierIconSearchClassnames = classes('icon-search-magnifier');

        const icon = (
            <SearchIcon
                className={svgIconSearchClassnames}
                pathMagnifierClassName={magnifierIconSearchClassnames}
                pathCircleClassName={circleIconSearchClassnames}
            />
        );

        if (!isBesideInput) {
            const { searchButtonLabel } = translations;
            const buttonClassnames = classes('gnav-search-button', {
                'elc-gnav-search-input-button': isBesideInput
            });

            return (
                <SearchButton
                    onClick={onClick}
                    className={buttonClassnames}
                    aria-expanded={isSearchOverlayOpen}
                    aria-label={searchButtonLabel}
                    data-test-id="SEA-Icon"
                    title={searchButtonLabel}
                >
                    {icon}
                </SearchButton>
            );
        }

        return (
            <MagnifierWrapper
                className={classes('gnav-search-icon-wrapper')}
                data-test-id="SEA-Icon"
            >
                {icon}
            </MagnifierWrapper>
        );
    })
);

export const SearchOverlayActiveScreen = observer(
    ({
        showDesktopInput: isInputVisibleOnDesktop = false,
        showMobileInput: isInputVisibleOnMobile = false,
        openSearchOverlay,
        isSearchOverlayOpen,
        inputConfig
    }: IOverlayIconProps) => (
        <SearchOverlayWrapper className={classes('search-icon-wrapper')}>
            <Desktop>
                <SearchMagnifier
                    onClick={!isInputVisibleOnDesktop && openSearchOverlay}
                    isBesideInput={isInputVisibleOnDesktop}
                    isSearchOverlayOpen={isSearchOverlayOpen}
                />
                {isInputVisibleOnDesktop && (
                    <GnavInput inputConfig={inputConfig} openSearchOverlay={openSearchOverlay} />
                )}
            </Desktop>
            <Mobile>
                <SearchMagnifier
                    onClick={openSearchOverlay}
                    isBesideInput={isInputVisibleOnMobile}
                    isSearchOverlayOpen={isSearchOverlayOpen}
                />
                {isInputVisibleOnMobile && (
                    <GnavInput inputConfig={inputConfig} openSearchOverlay={openSearchOverlay} />
                )}
            </Mobile>
        </SearchOverlayWrapper>
    )
);
